/**
 * Email client API — filter bindings + cached message reads.
 *
 * Bindings are owned by the user (RLS enforces `user_id = auth.uid()`) so we
 * can write to them directly from the browser. Message reads go straight to
 * v9_email_message_cache. The actual provider fetch happens server-side via
 * cron + the email-sync function.
 */
import { getSupabase } from './supabase';

export type EmailProvider = 'gmail' | 'outlook';
export type EmailFilterKind = 'label' | 'folder' | 'category' | 'search';

export interface EmailFilterBinding {
  id: string;
  tenant_id: string;
  user_id: string;
  provider: EmailProvider;
  filter_kind: EmailFilterKind;
  filter_value: string;
  display_label: string;
  is_active: boolean;
  created_at: string;
}

export interface EmailMessage {
  id: string;
  binding_id: string;
  external_id: string;
  from_email: string | null;
  from_name: string | null;
  subject: string | null;
  snippet: string | null;
  received_at: string;
  is_unread: boolean;
  has_attachments: boolean;
}

export interface FilterPickerOption {
  provider: EmailProvider;
  filter_kind: EmailFilterKind;
  filter_value: string;
  display_label: string;
  meta?: { unread?: number; total?: number };
}

interface FilterListResponse {
  gmail: { connected: boolean; labels: { id: string; name: string; type: string; messagesUnread?: number; messagesTotal?: number }[]; error?: string };
  outlook: { connected: boolean; folders: { id: string; displayName: string; unreadItemCount: number; totalItemCount: number }[]; error?: string };
}

async function authHeaders(): Promise<Record<string, string>> {
  const { data } = await getSupabase().auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('[email] not authenticated');
  return { authorization: `Bearer ${token}`, 'content-type': 'application/json' };
}

export async function listBindings(tenantId: string): Promise<EmailFilterBinding[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('v9_email_filter_bindings')
    .select('id, tenant_id, user_id, provider, filter_kind, filter_value, display_label, is_active, created_at')
    .eq('tenant_id', tenantId)
    .order('created_at', { ascending: true });
  if (error) throw new Error(error.message);
  return (data ?? []) as EmailFilterBinding[];
}

export async function listMessages(bindingId: string, limit = 20): Promise<EmailMessage[]> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('v9_email_message_cache')
    .select('id, binding_id, external_id, from_email, from_name, subject, snippet, received_at, is_unread, has_attachments')
    .eq('binding_id', bindingId)
    .order('received_at', { ascending: false })
    .limit(limit);
  if (error) throw new Error(error.message);
  return (data ?? []) as EmailMessage[];
}

export interface CreateBindingInput {
  tenantId: string;
  userId: string;
  provider: EmailProvider;
  filter_kind: EmailFilterKind;
  filter_value: string;
  display_label: string;
}

export async function createBinding(input: CreateBindingInput): Promise<EmailFilterBinding> {
  const supabase = getSupabase();
  const { data, error } = await supabase
    .from('v9_email_filter_bindings')
    .insert({
      tenant_id: input.tenantId,
      user_id: input.userId,
      provider: input.provider,
      filter_kind: input.filter_kind,
      filter_value: input.filter_value,
      display_label: input.display_label,
      is_active: true,
    })
    .select(
      'id, tenant_id, user_id, provider, filter_kind, filter_value, display_label, is_active, created_at',
    )
    .single();
  if (error) throw new Error(error.message);
  return data as EmailFilterBinding;
}

export async function deleteBinding(bindingId: string): Promise<void> {
  const supabase = getSupabase();
  const { error } = await supabase
    .from('v9_email_filter_bindings')
    .delete()
    .eq('id', bindingId);
  if (error) throw new Error(error.message);
}

/**
 * Picker data — Gmail labels + Outlook folders for the FilterPicker UI.
 */
export async function listAvailableFilters(): Promise<FilterListResponse> {
  const res = await fetch('/.netlify/functions/email-list-filters', {
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`list filters failed (${res.status})`);
  return (await res.json()) as FilterListResponse;
}

export async function triggerEmailSync(bindingId?: string): Promise<unknown> {
  const url = new URL('/.netlify/functions/email-sync', window.location.origin);
  if (bindingId) url.searchParams.set('bindingId', bindingId);
  const res = await fetch(url.toString().replace(window.location.origin, ''), {
    method: 'POST',
    headers: await authHeaders(),
  });
  if (!res.ok) throw new Error(`email-sync failed (${res.status})`);
  return await res.json();
}

/**
 * Best-effort external link to open a message in the user's webmail.
 * Gmail uses thread IDs; Outlook stores webLink in the raw payload but we
 * don't surface it via the cache shape — fall back to the inbox URL.
 */
export function externalMessageUrl(provider: EmailProvider, externalId: string): string {
  if (provider === 'gmail') {
    return `https://mail.google.com/mail/u/0/#all/${encodeURIComponent(externalId)}`;
  }
  return `https://outlook.office.com/mail/inbox/id/${encodeURIComponent(externalId)}`;
}
